import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaLock, FaArrowRight } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import Reveal from './ui/Reveal';
import ProjectCover from './ui/ProjectCover';
import ProjectWheel from './ProjectWheel';
import { getTechIcon } from './ui/techIcon';
import { useI18n } from '../context/LanguageContext';

const Section = styled.section`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 6rem 2rem;
  @media (max-width: 768px) { padding: 4.5rem 1.25rem; }
`;

const Head = styled.div`
  text-align: center; margin-bottom: 3rem;
  h2 { font-size: clamp(2rem, 4.5vw, 2.9rem); margin-bottom: 1rem; }
  p { color: var(--text-muted); max-width: 580px; margin: 0 auto; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.6rem;
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const Card = styled(motion.article)`
  position: relative;
  display: flex; flex-direction: column;
  border: 1px solid var(--border); background: var(--surface);
  border-radius: 22px; overflow: hidden; cursor: pointer;
  grid-column: ${(p) => (p.$featured ? 'span 2' : 'span 1')};
  transition: border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
  &:hover { transform: translateY(-6px); border-color: var(--primary-light); box-shadow: var(--card-shadow); }

  @media (max-width: 820px) { grid-column: span 1; }
`;

const Featured = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$featured ? '1.1fr 1fr' : '1fr')};
  height: 100%;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const Thumb = styled.div`
  position: relative; overflow: hidden;
  min-height: ${(p) => (p.$featured ? '260px' : 'auto')};
  aspect-ratio: ${(p) => (p.$featured ? 'auto' : '16 / 9')};
  img, video { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; }
  ${Card}:hover & img, ${Card}:hover & video { transform: scale(1.05); }
  &::after { content: ''; position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(7,10,20,0.55), transparent 55%); }
`;

const Tag = styled.span`
  position: absolute; top: 0.9rem; left: 0.9rem; z-index: 2;
  padding: 0.3rem 0.75rem; border-radius: 999px;
  font-size: 0.7rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
  color: #fff; background: rgba(7,10,20,0.6); border: 1px solid var(--border-strong); backdrop-filter: blur(6px);
`;

const Body = styled.div`
  display: flex; flex-direction: column; flex: 1; padding: 1.5rem;
  .org { font-size: 0.8rem; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--primary-light); margin-bottom: 0.4rem; }
  h3 { font-size: ${(p) => (p.$featured ? '1.5rem' : '1.2rem')}; margin-bottom: 0.55rem; }
  p { color: var(--text-muted); font-size: 0.95rem; line-height: 1.6; flex: 1; }
`;

const Badges = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.4rem; margin: 1.1rem 0 1.2rem;
  .badge {
    display: inline-flex; align-items: center; gap: 0.35rem;
    padding: 0.28rem 0.6rem; border-radius: 8px; font-size: 0.76rem; font-weight: 500;
    color: var(--text); background: var(--bg-alt); border: 1px solid var(--border);
  }
  .badge svg { font-size: 0.9rem; color: var(--primary-light); }
  .more { color: var(--text-faint); align-self: center; font-size: 0.76rem; }
`;

const More = styled.span`
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-size: 0.9rem; font-weight: 600; color: var(--text);
  svg { transition: transform 0.25s ease; }
  ${Card}:hover & svg { transform: translateX(4px); }
`;

/* ---------- Modal ---------- */
const Overlay = styled(motion.div)`
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(3, 6, 14, 0.7); backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center; padding: 1.5rem;
`;

const Modal = styled(motion.div)`
  position: relative; width: 100%; max-width: 720px; max-height: 88vh; overflow-y: auto;
  border: 1px solid var(--border-strong); background: var(--bg-alt);
  border-radius: 22px;
  box-shadow: 0 40px 90px -30px rgba(0,0,0,0.8);
`;

const ModalImg = styled.div`
  position: relative; aspect-ratio: 16 / 8; overflow: hidden;
  border-radius: 22px 22px 0 0;
  img, video { width: 100%; height: 100%; object-fit: cover; }
  &::after { content: ''; position: absolute; inset: 0;
    background: linear-gradient(to top, var(--bg-alt), transparent 60%); }
`;

const Close = styled.button`
  position: absolute; top: 1rem; right: 1rem; z-index: 3;
  display: grid; place-items: center; width: 40px; height: 40px; border-radius: 50%;
  border: 1px solid var(--border-strong); background: rgba(7,10,20,0.6); color: #fff;
  cursor: pointer; font-size: 1.1rem; backdrop-filter: blur(6px);
  transition: background 0.2s ease;
  &:hover { background: rgba(7,10,20,0.85); }
`;

const ModalBody = styled.div`
  padding: 0 2rem 2rem;
  margin-top: -2.5rem; position: relative; z-index: 2;
  .org { font-size: 0.82rem; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--primary-light); }
  h3 { font-size: 1.7rem; margin: 0.3rem 0 0.2rem; }
  .period { color: var(--text-faint); font-size: 0.9rem; margin-bottom: 1.5rem; }
`;

const Block = styled.div`
  margin-bottom: 1.3rem;
  h4 {
    font-family: var(--font-body); font-size: 0.74rem; font-weight: 700;
    letter-spacing: 0.14em; text-transform: uppercase; color: var(--secondary); margin-bottom: 0.45rem;
  }
  p { color: var(--text-muted); line-height: 1.7; }
`;

const ModalBadges = styled(Badges)`
  margin: 0.5rem 0 1.6rem;
`;

const ModalActions = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.75rem;
  a, .private {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.75rem 1.3rem; border-radius: 10px; font-weight: 600; font-size: 0.92rem;
  }
  .live { background: var(--gradient-accent); color: #fff; }
  .code { background: var(--bg); border: 1px solid var(--border-strong); color: var(--text); }
  .code:hover { border-color: var(--primary-light); }
  .private { color: var(--text-faint); border: 1px dashed var(--border-strong); }
`;

const MAX_BADGES = 4;

const Projects = () => {
  const { t, data } = useI18n();
  const { projects } = data;
  const [openId, setOpenId] = useState(null);
  const open = projects.find((p) => p.id === openId);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <Section id="projects">
      <Reveal>
        <Head>
          <span className="eyebrow">{t.projects.eyebrow}</span>
          <h2>{t.projects.h2a}<span className="gradient-text">{t.projects.h2b}</span></h2>
          <p>{t.projects.sub}</p>
        </Head>
      </Reveal>

      <Reveal>
        <ProjectWheel items={projects} onOpen={setOpenId} hint={t.projects.hint} />
      </Reveal>

      <Grid>
        {projects.map((p, i) => (
          <Card
            key={p.id}
            $featured={p.featured}
            onClick={() => setOpenId(p.id)}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: i * 0.06, ease: 'easeOut' }}
          >
            <Featured $featured={p.featured}>
              <Thumb $featured={p.featured}>
                <Tag>{p.tag}</Tag>
                {p.video
                  ? <video src={p.video} autoPlay muted loop playsInline />
                  : p.image
                    ? <img src={p.image} alt={p.title} loading="lazy" />
                    : <ProjectCover cover={p.cover} org={p.org} />}
              </Thumb>
              <Body $featured={p.featured}>
                <span className="org">{p.org}</span>
                <h3>{p.title}</h3>
                <p>{p.summary}</p>
                <Badges>
                  {p.tech.slice(0, MAX_BADGES).map((t) => (
                    <span className="badge" key={t}>{getTechIcon(t)}{t}</span>
                  ))}
                  {p.tech.length > MAX_BADGES && <span className="more">+{p.tech.length - MAX_BADGES}</span>}
                </Badges>
                <More>{t.projects.readCase} <FaArrowRight /></More>
              </Body>
            </Featured>
          </Card>
        ))}
      </Grid>

      <AnimatePresence>
        {open && (
          <Overlay
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpenId(null)}
          >
            <Modal
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Close onClick={() => setOpenId(null)} aria-label="Close"><FiX /></Close>
              <ModalImg>
                {open.video
                  ? <video src={open.video} autoPlay muted loop playsInline />
                  : open.image
                    ? <img src={open.image} alt={open.title} />
                    : <ProjectCover cover={open.cover} org={open.org} />}
              </ModalImg>
              <ModalBody>
                <span className="org">{open.org}</span>
                <h3>{open.title}</h3>
                <div className="period">{open.period}</div>

                <Block><h4>{t.projects.challenge}</h4><p>{open.problem}</p></Block>
                <Block><h4>{t.projects.what}</h4><p>{open.approach}</p></Block>
                <Block><h4>{t.projects.result}</h4><p>{open.result}</p></Block>

                <h4 style={{ fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-faint)', marginBottom: '0.5rem' }}>{t.projects.tech}</h4>
                <ModalBadges>
                  {open.tech.map((t) => (
                    <span className="badge" key={t}>{getTechIcon(t)}{t}</span>
                  ))}
                </ModalBadges>

                <ModalActions>
                  {open.links.live && (
                    <a className="live" href={open.links.live} target="_blank" rel="noopener noreferrer">
                      <FaExternalLinkAlt /> {t.projects.viewLive}
                    </a>
                  )}
                  {open.links.code && (
                    <a className="code" href={open.links.code} target="_blank" rel="noopener noreferrer">
                      <FaGithub /> {t.projects.viewCode}
                    </a>
                  )}
                  {!open.links.live && !open.links.code && (
                    <span className="private"><FaLock /> {open.privateNote || t.projects.privateFallback}</span>
                  )}
                </ModalActions>
              </ModalBody>
            </Modal>
          </Overlay>
        )}
      </AnimatePresence>
    </Section>
  );
};

export default Projects;
